import { resolveFirmwarePlan } from "./firmware-plans.js";
import { MAX_RECOVERY_FILE_BYTES, parseTalosUsbQueueExport } from "./ble-export.js";

function wait(milliseconds) {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

function encodeBase64(value) {
  const bytes = new TextEncoder().encode(value);
  let binary = "";
  for (let index = 0; index < bytes.length; index += 8192) {
    binary += String.fromCharCode(...bytes.subarray(index, index + 8192));
  }
  return window.btoa(binary);
}

async function loadFirmwareImages(plan) {
  return Promise.all(plan.images.map(async (image) => {
    const response = await fetch(image.path, { cache: "no-store" });
    if (!response.ok) throw new Error("Talos firmware files are not available on this release.");
    return { address: image.address, data: new Uint8Array(await response.arrayBuffer()) };
  }));
}

async function loadApplicationImage(plan) {
  const application = plan.images.find((image) => image.address === 0x10000);
  if (!application) throw new Error("This Talos release does not contain an application image.");
  const response = await fetch(application.path, { cache: "no-store" });
  if (!response.ok) throw new Error("Talos firmware files are not available on this release.");
  return { address: application.address, data: new Uint8Array(await response.arrayBuffer()) };
}

async function openSerialPortWhenReady(port, timeoutMilliseconds = 12000) {
  const deadline = Date.now() + timeoutMilliseconds;
  let lastError;
  while (Date.now() < deadline) {
    try {
      await port.open({ baudRate: 115200, bufferSize: 65536 });
      return;
    } catch (error) {
      lastError = error;
      await wait(500);
    }
  }
  throw new Error(lastError?.message
    ? `The ESP32 did not reconnect after flashing: ${lastError.message}`
    : "The ESP32 did not reconnect after flashing.");
}

async function closeSerialPortIfOpen(port) {
  if (port.readable || port.writable) await port.close().catch(() => undefined);
}

async function rebootIntoApplication(port, onLog) {
  if (!port.readable && !port.writable) await openSerialPortWhenReady(port);
  onLog("Restarting the ESP32 in Talos application mode.");
  await port.setSignals({ dataTerminalReady: false, requestToSend: true });
  await wait(120);
  await port.setSignals({ dataTerminalReady: false, requestToSend: false }).catch(() => undefined);
  await wait(300);
  await closeSerialPortIfOpen(port);
  await wait(900);
  await openSerialPortWhenReady(port);
  await port.setSignals({ dataTerminalReady: false, requestToSend: false }).catch(() => undefined);
  await wait(350);
}

async function reconnectAfterDeviceRestart(port) {
  await wait(350);
  await closeSerialPortIfOpen(port);
  await wait(900);
  await openSerialPortWhenReady(port, 18000);
  await port.setSignals({ dataTerminalReady: false, requestToSend: false }).catch(() => undefined);
}

function cleanLine(value) {
  return Array.from(value).filter((character) => {
    const code = character.charCodeAt(0);
    return code === 9 || (code >= 32 && code !== 127);
  }).join("").trim();
}

async function readUntil(port, expected, onLog, timeoutMilliseconds) {
  if (!port.readable) throw new Error("The serial connection did not reopen after flashing.");
  const reader = port.readable.getReader();
  const decoder = new TextDecoder();
  let buffered = "";
  let matched = false;
  const deadline = Date.now() + timeoutMilliseconds;
  try {
    while (Date.now() < deadline) {
      const remaining = deadline - Date.now();
      const result = await Promise.race([
        reader.read(),
        new Promise((_, reject) => window.setTimeout(() => reject(new Error("Timed out waiting for the ESP32.")), remaining)),
      ]);
      if (result.done) break;
      buffered += decoder.decode(result.value, { stream: true });
      const lines = buffered.split(/\r?\n/);
      buffered = lines.pop() ?? "";
      for (const rawLine of lines) {
        const line = cleanLine(rawLine);
        if (!line) continue;
        onLog(line);
        if (expected.some((marker) => line.includes(marker))) {
          matched = true;
          return line;
        }
      }
    }
    throw new Error("The ESP32 did not confirm provisioning. Keep it connected and try again.");
  } finally {
    if (!matched) await reader.cancel().catch(() => undefined);
    reader.releaseLock();
  }
}

async function writeSerialLine(port, line) {
  if (!port.writable) throw new Error("The serial connection is not writable.");
  const writer = port.writable.getWriter();
  try {
    const bytes = new TextEncoder().encode(`${line}\n`);
    for (let offset = 0; offset < bytes.length; offset += 48) {
      await writer.write(bytes.subarray(offset, offset + 48));
      if (offset + 48 < bytes.length) await wait(12);
    }
  } finally {
    writer.releaseLock();
  }
}

async function readSerialChunk(reader, timeoutMilliseconds) {
  let timeoutId;
  try {
    return await Promise.race([
      reader.read(),
      new Promise((_, reject) => {
        timeoutId = window.setTimeout(() => reject(new Error("timeout")), timeoutMilliseconds);
      }),
    ]);
  } finally {
    if (timeoutId) window.clearTimeout(timeoutId);
  }
}

async function readUsbQueueExport(port, expectedDeviceCode, timeoutMilliseconds = 180000) {
  if (!port.readable) throw new Error("The USB connection is not readable.");
  const reader = port.readable.getReader();
  const decoder = new TextDecoder();
  const marker = "TALOS-OFFLINE/1\n";
  let buffered = "";
  let exportStarted = false;
  let complete = false;
  let deadline = Date.now() + 8000;
  try {
    while (Date.now() < deadline) {
      const remaining = deadline - Date.now();
      const result = await readSerialChunk(reader, remaining);
      if (result.done) break;
      buffered = `${buffered}${decoder.decode(result.value, { stream: true })}`.replaceAll("\r\n", "\n");
      const markerIndex = buffered.indexOf(marker);
      if (markerIndex >= 0) {
        if (!exportStarted) deadline = Date.now() + timeoutMilliseconds;
        exportStarted = true;
        if (markerIndex > 0) buffered = buffered.slice(markerIndex);
      }
      if (!exportStarted && (buffered.includes("TALOS_COMMAND_UNKNOWN") || buffered.includes("TALOS_QUEUE_EXPORT_FAILED"))) {
        const error = new Error(buffered.includes("not_configured")
          ? "This logger is not configured, so there is no assigned reading queue to back up."
          : "The installed firmware does not support a verified USB queue backup yet.");
        error.code = buffered.includes("not_configured") ? "QUEUE_EXPORT_FAILED" : "QUEUE_EXPORT_UNSUPPORTED";
        throw error;
      }
      if (exportStarted && buffered.length > MAX_RECOVERY_FILE_BYTES) {
        throw new Error("The logger's stored-reading backup is larger than the 2 MB safety limit. The firmware update was stopped before writing anything.");
      }
      if (exportStarted && buffered.includes("\nEND\n")) {
        const end = buffered.indexOf("\nEND\n") + 5;
        complete = true;
        return parseTalosUsbQueueExport(buffered.slice(0, end), expectedDeviceCode);
      }
    }
    const error = new Error(exportStarted
      ? "The USB backup ended before every stored reading was copied. The firmware update was stopped before writing anything."
      : "The installed firmware does not support a verified USB queue backup yet.");
    error.code = exportStarted ? "QUEUE_EXPORT_INCOMPLETE" : "QUEUE_EXPORT_UNSUPPORTED";
    throw error;
  } catch (error) {
    if (error?.message === "timeout") {
      const timeoutError = new Error(exportStarted
        ? "The USB backup timed out before every stored reading was copied. The firmware update was stopped before writing anything."
        : "The installed firmware does not support a verified USB queue backup yet.");
      timeoutError.code = exportStarted ? "QUEUE_EXPORT_INCOMPLETE" : "QUEUE_EXPORT_UNSUPPORTED";
      throw timeoutError;
    }
    throw error;
  } finally {
    if (!complete) await reader.cancel().catch(() => undefined);
    reader.releaseLock();
  }
}

async function captureUsbQueueBackup(port, expectedDeviceCode, onLog) {
  try {
    await openSerialPortWhenReady(port);
    await port.setSignals({ dataTerminalReady: false, requestToSend: false }).catch(() => undefined);
    await wait(1500);
    onLog("Copying unsent readings from the logger before the firmware update.");
    await writeSerialLine(port, "TALOS_QUEUE_EXPORT");
    const backup = await readUsbQueueExport(port, expectedDeviceCode);
    onLog(`USB backup verified: ${backup.records.length} unsent reading${backup.records.length === 1 ? "" : "s"}.`);
    return { supported: true, ...backup };
  } catch (error) {
    if (error?.code !== "QUEUE_EXPORT_UNSUPPORTED") throw error;
    onLog("The installed firmware predates USB queue backup. Talos will preserve its data partition and verify the logger after updating.");
    return { supported: false, deviceCode: expectedDeviceCode, records: [] };
  } finally {
    await closeSerialPortIfOpen(port);
    await wait(500);
  }
}

async function provisionConnectedTalos({ port, provisioning, wifiSsid, wifiPassword, cellular, onLog, onStage }) {
  onStage("provisioning");
  await rebootIntoApplication(port, onLog);
  await writeSerialLine(port, "TALOS_INFO");
  await readUntil(port, ["TALOS_INFO"], onLog, 10000);
  await wait(700);
  const localProvisioning = {
    ...provisioning,
    wifi_ssid: wifiSsid,
    wifi_password: wifiPassword,
    ...(provisioning.connectivity_type === "cellular" ? {
      cellular_apn: cellular?.apn?.trim() ?? "",
      cellular_user: cellular?.user?.trim() ?? "",
      cellular_password: cellular?.password ?? "",
      cellular_sim_pin: cellular?.simPin?.trim() ?? "",
    } : {}),
  };
  const command = `TALOS_PROVISION ${encodeBase64(JSON.stringify(localProvisioning))}`;
  onLog(`Sending protected configuration packet (${new TextEncoder().encode(command).length} bytes).`);
  await writeSerialLine(port, command);
  const response = await readUntil(port, ["TALOS_PROVISIONED", "TALOS_PROVISION_FAILED"], onLog, 20000);
  if (response.includes("TALOS_PROVISION_FAILED")) {
    throw new Error(`The ESP32 rejected the protected configuration: ${response.replace("TALOS_PROVISION_FAILED", "").trim() || "unknown reason"}.`);
  }
  await writeSerialLine(port, "TALOS_REBOOT");

  onStage("verifying");
  await reconnectAfterDeviceRestart(port);
  await readUntil(port, ["TALOS_WIFI_PORTAL", "TALOS_WIFI_CONNECTED", "TALOS_CELLULAR_CONNECTED", "TALOS_CELLULAR_SETUP_REQUIRED", "TALOS_READY", "TALOS_TEMP"], onLog, 45000);
  onStage("complete");
}

export function supportsTalosInstaller() {
  return Boolean(window.isSecureContext && navigator.serial);
}

export async function flashAndProvisionTalos({ provisioning, wifiSsid, wifiPassword, cellular, onLog, onProgress, onStage }) {
  if (!supportsTalosInstaller()) throw new Error("Use desktop Chrome on HTTPS to install Talos over USB.");
  const { ESPLoader, Transport } = await import("esptool-js");
  const port = await navigator.serial.requestPort();
  const transport = new Transport(port, false);
  const terminal = {
    clean() {},
    write(value) { if (cleanLine(value)) onLog(cleanLine(value)); },
    writeLine(value) { if (cleanLine(value)) onLog(cleanLine(value)); },
  };

  try {
    onStage("connecting");
    const loader = new ESPLoader({ transport, baudrate: 115200, terminal, debugLogging: false });
    const chipName = await loader.main();
    const plan = await resolveFirmwarePlan(chipName, fetch, provisioning.hardware_name);
    onLog(`Detected ${chipName}. Selected ${plan.label} firmware.`);
    const images = await loadFirmwareImages(plan);

    onStage("flashing");
    await loader.writeFlash({
      fileArray: images,
      flashMode: plan.flashMode,
      flashFreq: plan.flashFrequency,
      flashSize: plan.flashSize,
      eraseAll: true,
      compress: true,
      reportProgress: (_fileIndex, written, total) => onProgress(Math.min(100, Math.round((written / total) * 100))),
    });
    await transport.disconnect();

    await provisionConnectedTalos({ port, provisioning, wifiSsid, wifiPassword, cellular, onLog, onStage });
    return { port, chipName, version: plan.version };
  } catch (error) {
    if (port.readable || port.writable) await port.close().catch(() => undefined);
    throw error;
  }
}

export async function provisionInstalledTalos({ provisioning, wifiSsid, wifiPassword, cellular, onLog, onStage }) {
  if (!supportsTalosInstaller()) throw new Error("Use desktop Chrome on HTTPS to provision Talos over USB.");
  const port = await navigator.serial.requestPort();
  try {
    onStage("connecting");
    await openSerialPortWhenReady(port);
    await provisionConnectedTalos({ port, provisioning, wifiSsid, wifiPassword, cellular, onLog, onStage });
    return { port };
  } catch (error) {
    await closeSerialPortIfOpen(port);
    throw error;
  }
}

export async function upgradeInstalledTalos({ hardwareName = null, deviceCode = "", onLog, onProgress, onStage }) {
  if (!supportsTalosInstaller()) throw new Error("Use desktop Chrome on HTTPS to update Talos over USB.");
  const { ESPLoader, Transport } = await import("esptool-js");
  const port = await navigator.serial.requestPort();
  const terminal = {
    clean() {},
    write(value) { if (cleanLine(value)) onLog(cleanLine(value)); },
    writeLine(value) { if (cleanLine(value)) onLog(cleanLine(value)); },
  };

  try {
    onStage("connecting");
    const queueBackup = await captureUsbQueueBackup(port, deviceCode, onLog);
    const transport = new Transport(port, false);
    const loader = new ESPLoader({ transport, baudrate: 115200, terminal, debugLogging: false });
    const chipName = await loader.main();
    const plan = await resolveFirmwarePlan(chipName, fetch, hardwareName);
    onLog(`Detected ${chipName}. Updating ${plan.label} without erasing its settings.`);
    const application = await loadApplicationImage(plan);

    onStage("flashing");
    await loader.writeFlash({
      fileArray: [application],
      flashMode: plan.flashMode,
      flashFreq: plan.flashFrequency,
      flashSize: plan.flashSize,
      eraseAll: false,
      compress: true,
      reportProgress: (_fileIndex, written, total) => onProgress(Math.min(100, Math.round((written / total) * 100))),
    });
    await transport.disconnect();

    onStage("verifying");
    await rebootIntoApplication(port, onLog);
    await readUntil(port, ["TALOS_READY"], onLog, 30000);
    await closeSerialPortIfOpen(port);
    onStage("complete");
    return { chipName, version: plan.version, queueBackup };
  } catch (error) {
    await closeSerialPortIfOpen(port);
    throw error;
  }
}

export async function startTalosSerialMonitor(port, onLog) {
  if (!port) throw new Error("Connect and provision the ESP32 before opening the monitor.");
  if (!port.readable) await port.open({ baudRate: 115200, bufferSize: 65536 });
  const reader = port.readable.getReader();
  const decoder = new TextDecoder();
  let stopped = false;
  let buffered = "";
  const completed = (async () => {
    try {
      while (!stopped) {
        const { value, done } = await reader.read();
        if (done || !value) break;
        buffered += decoder.decode(value, { stream: true });
        const lines = buffered.split(/\r?\n/);
        buffered = lines.pop() ?? "";
        lines.map(cleanLine).filter(Boolean).forEach(onLog);
      }
    } finally {
      reader.releaseLock();
    }
  })();
  return {
    async stop() {
      stopped = true;
      await reader.cancel().catch(() => undefined);
      await completed.catch(() => undefined);
      if (port.readable || port.writable) await port.close().catch(() => undefined);
    },
  };
}
